var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { OnChainPermissions } from "healthex-common/auth.dtos";
import { AccountStatus, UserPermissions, UserType } from "healthex-common/user.dtos";
import { getFullName, willNeverHappen } from "healthex-common/utils";
import { autorun, computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { ConfirmUserDeleteDialogModel } from "./ConfirmUserDeleteDialog";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { isErr } from "healthex-common/result";
export class AdminUserEditFormModel {
    constructor(opts) {
        this.deleteConfirmationDialog = new ConfirmUserDeleteDialogModel();
        this.resetPasswordConfirmationDialog = new SimpleDialogModel();
        this.formModel = new FormModel({
            email: "",
            phone: "",
            firstName: "",
            lastName: "",
            title: "",
            permissions: [],
            onChainAddress: "",
            avatarUrl: "",
            newAvatar: null,
            providerId: "",
        }, ["email", "firstName", "lastName"]);
        this.organization = NOT_LOADED;
        this.user = NOT_LOADED;
        this.isUserInvited = false;
        this.organizationsManager = opts.container.organizationsManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.userId = opts.userId;
        this.onSave = opts.onSave;
        this.onCancel = opts.onCancel;
        void this.loadUser();
        autorun(async () => {
            this.isUserInvited = await this.organizationsManager.isUserInvited(this.userId);
        });
    }
    get blockchainAddressText() {
        if (this.isUserInvited) {
            return "User has been sent an invite to provision Blockchain Address";
        }
        return this.formModel.input.onChainAddress;
    }
    get userFullName() {
        if (this.user === NOT_LOADED)
            return null;
        return getFullName(this.user);
    }
    async loadUser(reload) {
        var _a;
        if (reload) {
            await this.organizationsManager.waitForReloadOrganizations();
        }
        const organization = this.organizationsManager.myOrganization.value;
        if (organization === NOT_LOADED)
            return;
        if (!organization)
            return;
        this.organization = organization;
        const user = (_a = organization.users) === null || _a === void 0 ? void 0 : _a.find((candidate) => candidate.id === this.userId);
        if (!user)
            return;
        this.user = user;
        const { email, phone, firstName, lastName, title, permissions, onChainAddress, avatarUrl, onChainUser, type, providerId } = user;
        const onChainPermissions = onChainUser ? [OnChainPermissions.ON_CHAIN_ACTIONS] : [];
        const researcherPermission = type === UserType.EXTERNAL_RESEARCHER && !permissions.includes(UserPermissions.RESEARCHER) ? [UserPermissions.RESEARCHER] : [];
        this.formModel.input = {
            email,
            phone,
            firstName: firstName !== null && firstName !== void 0 ? firstName : "",
            lastName: lastName !== null && lastName !== void 0 ? lastName : "",
            title,
            permissions: [...permissions, ...onChainPermissions, ...researcherPermission],
            onChainAddress: onChainAddress !== null && onChainAddress !== void 0 ? onChainAddress : "",
            avatarUrl,
            newAvatar: null,
            providerId: providerId !== null && providerId !== void 0 ? providerId : "",
        };
    }
    getCreateUserInput() {
        const { email, phone, firstName, lastName, title, permissions, onChainAddress, newAvatar, providerId } = this.formModel.input;
        const standardPermissions = permissions.filter((permission) => permission !== OnChainPermissions.ON_CHAIN_ACTIONS);
        const updatedUser = {
            email,
            phone,
            firstName,
            lastName,
            title,
            permissions: standardPermissions,
            onChainAddress: onChainAddress === "" ? null : onChainAddress,
            newAvatar,
            onChainUser: permissions.includes(OnChainPermissions.ON_CHAIN_ACTIONS),
            providerId,
        };
        return updatedUser;
    }
    get permissionsToShow() {
        if (this.user === NOT_LOADED || this.user === null)
            return [];
        switch (this.user.type) {
            case UserType.HEALTHCARE_ORGANIZATION:
                return [
                    {
                        permission: UserPermissions.RESEARCHER,
                        label: "Researcher",
                        tooltip: "Researchers can create studies and recruit patients for research efforts.",
                    },
                    {
                        permission: UserPermissions.PROVIDER,
                        label: "Provider",
                        tooltip: "Providers can guide patients through the informed consent experience to obtain patient consent for research efforts.",
                    },
                    {
                        permission: UserPermissions.RESEARCH_ADMIN,
                        label: "Research Admin",
                        tooltip: "Research admins can manage organization and research settings, review research proposals, and monitor ongoing research.",
                    },
                    {
                        permission: UserPermissions.ORG_ADMIN,
                        label: "IT Admin",
                        tooltip: "IT admins can manage organization settings, user accounts, data integrations, and IT configurations.",
                    },
                ];
            case UserType.EXTERNAL_RESEARCHER:
                return [
                    {
                        permission: UserPermissions.RESEARCHER,
                        label: "User",
                        hardcodedToTrue: true,
                        tooltip: "Every user added to your organization on HealthEx is a user. Users can create projects and recruit patients.",
                    },
                    {
                        permission: UserPermissions.ORG_ADMIN,
                        label: "Admin",
                        tooltip: "Admins can manage organization settings, user accounts, research settings, and IT configurations.",
                    },
                ];
            case UserType.SERVICE_PROVIDER:
                return [
                    {
                        permission: UserPermissions.ORG_ADMIN,
                        label: "Admin",
                        tooltip: "Admins can manage organization settings, user accounts, research settings, and IT configurations.",
                    },
                ];
        }
        return willNeverHappen(this.user);
    }
    get showResetPasswordButton() {
        if (this.user === NOT_LOADED || this.organization === NOT_LOADED)
            return false;
        return this.user.accountStatus === AccountStatus.ACTIVE && !this.organization.ssoEnabled;
    }
    get isProvider() {
        return this.formModel.input.permissions.includes(UserPermissions.PROVIDER);
    }
    validateInput() {
        this.formModel.clearErrors();
        const { notBlank, validEmail, validPhone } = validators;
        const f = this.formModel;
        f.validateField("email", notBlank, "Enter an email address");
        f.validateField("firstName", notBlank, "Enter a first name");
        f.validateField("lastName", notBlank, "Enter a last name");
        f.validateField("email", validEmail, "Enter a valid email address");
        f.validateField("phone", validPhone, "Enter a valid phone number");
        f.validateField("permissions", (permissions) => permissions.includes(UserPermissions.ORG_ADMIN) ||
            permissions.includes(UserPermissions.RESEARCH_ADMIN) ||
            permissions.includes(UserPermissions.RESEARCHER) ||
            permissions.includes(UserPermissions.PROVIDER), "Each user must be assigned a role");
        if (this.isProvider) {
            f.clearError("providerId");
            f.validateField("providerId", notBlank, "Enter a unique provider ID");
        }
        return !f.hasErrors();
    }
    async save() {
        const success = await this.performUpdate();
        if (success) {
            await this.loadUser(true);
            this.onSave();
        }
    }
    async performUpdate() {
        if (this.user === NOT_LOADED || this.user === null)
            return false;
        if (!this.validateInput())
            return false;
        const { email, phone, firstName, lastName, title, permissions, onChainAddress, newAvatar, providerId } = this.formModel.input;
        const standardPermissions = permissions.filter((permission) => permission !== OnChainPermissions.ON_CHAIN_ACTIONS);
        const updatedUser = {
            type: this.user.type,
            email,
            phone,
            firstName,
            lastName,
            title,
            permissions: standardPermissions,
            onChainAddress: onChainAddress === "" ? null : onChainAddress,
            newAvatar,
            onChainUser: permissions.includes(OnChainPermissions.ON_CHAIN_ACTIONS),
            providerId,
        };
        const result = await this.organizationsManager.editUser(this.user.organizationId, this.userId, updatedUser);
        if (isErr(result)) {
            switch (result.error) {
                case "EMAIL_ALREADY_TAKEN":
                    this.formModel.addError("email", "Email address is already taken");
                    break;
                case "INVALID_EMAIL":
                    this.formModel.addError("email", "Enter a valid email address");
                    break;
                case "PROVIDER_ID_ALREADY_TAKEN":
                    this.formModel.addError("providerId", "Enter a unique provider ID");
                    break;
            }
            return false;
        }
        const provisionAddress = this.formModel.input.permissions.includes(OnChainPermissions.ON_CHAIN_ACTIONS) && this.formModel.input.onChainAddress === "";
        if (provisionAddress) {
            try {
                await this.organizationsManager.sendInvite(this.userId);
            }
            catch (error) {
                console.error("sendInvite error", error);
            }
        }
        return true;
    }
    cancel() {
        this.onCancel();
    }
    get cannotBeDeletedReason() {
        if (this.user === NOT_LOADED)
            return null;
        const reason = this.organizationsManager.getCannotBeDeletedReason(this.user);
        if (reason === NOT_LOADED)
            return null;
        switch (reason) {
            case "SELF":
                return "You can’t remove yourself from the organization. Please ask another IT admin to complete this action.";
            case "ONLY_ORG_AND_RESEARCH_ADMIN": {
                return "You can’t remove this user as they’re the only reasearch and IT admin.";
            }
            case "ONLY_ORG_ADMIN": {
                return "You can’t remove this user as they’re the only IT admin.";
            }
            case "ONLY_RESEARCH_ADMIN": {
                return "You can’t remove this user as they’re the only research admin.";
            }
            case "ONLY_RESEARCHER_WITH_AT_LEAST_ONE_STUDY": {
                return "You can’t remove this user as they’re the only researcher.";
            }
            case null: {
                return null;
            }
        }
        return willNeverHappen(reason);
    }
    get providerTextFieldEnabled() {
        return this.isProvider;
    }
    async forcePasswordChange() {
        if (this.organization === NOT_LOADED || this.user === NOT_LOADED)
            return;
        const confirmed = await this.resetPasswordConfirmationDialog.showAndWait();
        if (!confirmed)
            return;
        await this.organizationsManager.forcePasswordReset(this.organization.id, this.user.id);
        this.bannerUIManager.displayBanner({ text: `${this.userFullName || this.user.email}'s password has been successfully reset` });
        this.onCancel();
    }
    async deleteUser() {
        if (this.organization === NOT_LOADED || this.user === NOT_LOADED)
            return;
        this.deleteConfirmationDialog.user = this.user;
        const confirmed = await this.deleteConfirmationDialog.showAndWait();
        if (confirmed) {
            await this.organizationsManager.deleteUser(this.organization.id, this.user.id);
            this.bannerUIManager.displayBanner({ text: `${this.userFullName || this.user.email}'s account successfully deleted` });
            this.onCancel();
        }
    }
    get orgName() {
        if (this.organization === NOT_LOADED)
            return null;
        return this.organization.name;
    }
}
__decorate([
    observable
], AdminUserEditFormModel.prototype, "formModel", void 0);
__decorate([
    observable
], AdminUserEditFormModel.prototype, "userId", void 0);
__decorate([
    observable
], AdminUserEditFormModel.prototype, "organization", void 0);
__decorate([
    observable
], AdminUserEditFormModel.prototype, "user", void 0);
__decorate([
    observable
], AdminUserEditFormModel.prototype, "isUserInvited", void 0);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "blockchainAddressText", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "userFullName", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "permissionsToShow", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "showResetPasswordButton", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "isProvider", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "cannotBeDeletedReason", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "providerTextFieldEnabled", null);
__decorate([
    computed
], AdminUserEditFormModel.prototype, "orgName", null);
